/** Historical Imports words for reading a registrar's own export: its columns, its derivation and its formats. */
import type { RecordsField } from "@/lib/historical-records-source";
import type { RosterField } from "@/lib/registrar-roster-source";
import { inSentence, type SourceField } from "@/lib/source-columns";

export const columnsWords = {
  recordsIntro: "Past terms, their sections, instructors, learners, enrollments and certified results. Bring IMAM's template, one row per record under your own keys, or your own export, one row per enrollment.",
  rosterIntro: "Bring IMAM's roster template, or your own export with one row per enrollment. Review each learner, then read a dry run. A roster creates no invitation.",
  columns: "Columns",
  noFileRead: "No file read",
  readAsWritten: "Read as written",
  columnNeeded: "A column is needed",
  columnsChosen: "Columns chosen",
  notInFile: "Not in this file",
  required: "Required",
  optional: "Optional",
  readFirst: "Read a file first.",
  asWritten: "This file is IMAM's template or typed rows, so its columns are read as written.",
  intro: "Your export is read one line per enrollment. Choose the column that holds each field. A column is suggested only when its header names the field exactly, and you can change every one.",
  howRowsAreRead: "How rows are read",
  readAgain: "Choose the file again to change its columns.",
  // Formats
  templateShape: "IMAM's template · one row per record",
  exportShape: "Your own export · one row per enrollment",
  exportNote: "Bring the export as your system writes it, saved as CSV. This example shows the fields it reads; your headers can differ.",
  downloadExample: "Download an export example",
  field: "Field",
  needed: "Needed",
} as const;

export const columnsText = {
  firstLine: (value: string) => `Line 2 reads ${JSON.stringify(value)}`,
  unread: (names: string[]) => `${names.length === 1 ? "This column is" : "These columns are"} not read: ${names.join(", ")}`,
  exportLines: (n: number) => `Read as one row per enrollment from ${n} ${n === 1 ? "line" : "lines"}`,
  chooseColumn: (field: SourceField) => `Choose the column that holds the ${inSentence(field.label)}.`,
  alsoReads: (aliases: readonly string[]) => aliases.length ? ` Suggested from a header named ${aliases.map((a) => JSON.stringify(a)).join(", ")}.` : "",
} as const;

/** The sentence that holds an import while a required field has no column. */
export const MISSING: Partial<Record<RecordsField | RosterField, string>> = {
  learner_source_id: "Choose the column that holds each learner's source ID. If your export has none, add one and read the file again: IMAM never matches a learner by name or email.",
  credits: "Choose the column that holds each section's credits. If your export has none, add one and read the file again: a section without credits cannot count toward a GPA.",
};

export const RECORDS_DERIVATION = [
  "One term per term name, as written.",
  "One section per term name and course code, and its section identifier when the line has one.",
  "One instructor per name as written, co-instructors separated by ;. You choose each instructor's record next.",
  "One learner per learner source ID. A name or an email never matches a learner.",
  "One enrollment per section and learner, and a certified result where the line has a result letter.",
  "Lines repeating the same values become one row. The same term, section, learner or enrollment with different values is listed with its lines.",
] as const;

export const ROSTER_DERIVATION = [
  "One student per IMAM SID, or for a new student, per given name, family name and delivery email.",
  "One enrollment per student, course code and term name. A line with neither is a student only.",
  "Lines repeating the same values become one row. The same student or enrollment with different values is listed with its lines.",
] as const;

export const RECORDS_FIELD_NOTES: Record<RecordsField, string> = {
  term_name: "The past term each line belongs to. Lines naming it the same way are one term.",
  term_start: "YYYY-MM-DD. The term is recorded as closed.",
  term_end: "YYYY-MM-DD.",
  course_code: "The institutional course code.",
  course_title: "The section's title.",
  credits: "Whole credits. A section without them cannot count toward a GPA.",
  section: "Tells apart two sections of one course in one term.",
  instructor_name: "As the source writes it. It never chooses a record: you choose each instructor's record.",
  co_instructors: "Names separated by ;",
  learner_source_id: "The learner's ID in your system. Required: a name or an email never matches a learner.",
  learner_name: "The learner's legal name.",
  email: "Contact evidence only. It never chooses or matches a person.",
  learner_status: "completed, active, leave of absence or withdrawn",
  enrollment_status: "complete, enrolled, withdrawn or incomplete",
  result_letter: "The certified result. A line without one records the enrollment only.",
  result_score: "Optional score 0-100, with its result letter.",
};

export const ROSTER_FIELD_NOTES: Record<RosterField, string> = {
  student_id: "The learner's existing IMAM SID. Blank issues a new SID.",
  given_name: "With an SID it corroborates the record; it never finds one.",
  family_name: "With an SID it corroborates the record; it never finds one.",
  delivery_email: "The contact a later invitation would use. Never an identity.",
  program_key: "The program's code.",
  cohort_key: "The cohort's name.",
  admitted_year: "Four digits. It sets a new SID's cohort.",
  course_code: "An existing section in an open term, with its term name.",
  term_name: "The open term the section belongs to.",
};
